'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';

import { Avatar } from '~/shared/components/Avatar';
import { ProfileDropdownProps } from '~/shared/types/UserDropdown';

import { Icon } from '../Icon';

import { ActionButtons } from './components/ActionButtons';
import { BasicInfo } from './components/BasicInfo';
import { TeamAndRoles } from './components/TeamAndRoles';

export default function ProfileDropdownV2({
  items,
  profileInfo,
}: ProfileDropdownProps) {
  const [isOpen, setOpen] = useState(false);
  const pathname = usePathname();

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef}>
      <div className="inline-block text-left hover:bg-[#ededef] transition-colors duration-100 ease-linear rounded-[4px]">
        <button
          type="button"
          onClick={() => setOpen(!isOpen)}
          className="flex items-center gap-2 h-12 min-w-12 pl-2.5 pr-2.5 text-base outline-none"
        >
          <Avatar
            size="sm"
            name={profileInfo.name}
            src={profileInfo.urlImage}
          />

          <Icon isCustom icon={isOpen ? 'caretUp' : 'caretDown'} size={16} />
        </button>
      </div>

      {isOpen && (
        <div className="fixed top-15 right-3 mt-4 w-74 origin-top-right rounded-xl bg-white py-2 px-2.5 gap-4 shadow-lg ring-1 ring-black/5 opacity-0 scale-95 animate-dropdown">
          <BasicInfo profileInfo={profileInfo} />
          <TeamAndRoles
            teamName={profileInfo.teamName}
          />
          <ActionButtons items={items} onClose={() => setOpen(false)} />
        </div>
      )}
    </div>
  );
}
